import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { translate, getEnvironmentLabel } from '../../utils/Common';
import NewsModal from '../common/NewsModal';
// contexts
import AuthContext from '../../contexts/auth';
import SidebarContext from '../../contexts/sidebar';
// icons
import {
	LuChevronDown,
	LuChevronsLeft,
	LuChevronsRight,
	LuX,
	LuMegaphone,
} from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip';
// sidebar info
import Submenu from './Submenu';
import Options from './Options/indexRoles';
import { BLI_PERMISSIONS_UPDATED_EVENT } from '../../config/bliPermissionsRuntime';
import UserMenu from '../common/UserMenu';
import { cn } from '@/lib/utils';
// version
import packageJson from '../../../package.json';

// The dashboard content is offset by these exact widths (see
// views/Dashboard/index.css: .main-layout-open / .main-layout), so they must
// not drift.
const WIDTH_OPEN = 'w-[260px]';
// 72px fits a 44px pill with 14px of gutter either side. It was 100px, which
// left the rail wider than anything in it.
const WIDTH_CLOSED = 'w-[72px]';

// Below 660px the rail would sit on top of the content, so the sidebar becomes
// a full-width overlay when open and slides off-canvas when closed. The bottom
// navigation takes over as the primary navigation at that size.
const MOBILE_OPEN = 'max-[660px]:w-full';
const MOBILE_CLOSED = 'max-[660px]:-translate-x-full';

const Sidebar = () => {
	const userContext = useContext(AuthContext);
	const sidebarContext = useContext(SidebarContext);
	const history = useHistory();
	const [bliMenuTick, setBliMenuTick] = useState(0);
	useEffect(() => {
		const onBliUpdated = () => setBliMenuTick((t) => t + 1);
		window.addEventListener(BLI_PERMISSIONS_UPDATED_EVENT, onBliUpdated);
		return () => window.removeEventListener(BLI_PERMISSIONS_UPDATED_EVENT, onBliUpdated);
	}, []);

	const SidebarData = useMemo(
		() => Options(userContext.user),
		[userContext.user, bliMenuTick]
	);
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
	const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
	const [hasNewUpdates, setHasNewUpdates] = useState(false);
	const userMenuRef = useRef(null);
	const isOpen = sidebarContext.sidebarOpen;

	const handleName = () => {
		if (userContext.user?.NombreSimple.indexOf(' ') > -1) return `${userContext.user?.NombreSimple.split(' ')[0]} ${userContext.user?.ApellidoSimple}`;
		else return `${userContext.user?.NombreSimple} ${userContext.user?.ApellidoSimple}`;
	}

	// Obtener bandera pequeña según el país elegido en login (localStorage.country)
	const getCountryFlag = () => {
		if (typeof window === 'undefined') return null;
		const country = (localStorage.getItem('country') || 'us').toLowerCase();
		if (country === 'pa') return '/img/pa_flag.png';
		if (country === 'br') return '/img/br_flag.png';
		if (country === 'us') return '/img/usa_flag.png';
		return null;
	};

	const countryFlag = getCountryFlag();

	// Rendered locally instead of fetched from ui-avatars.com. An internal admin
	// portal was calling a third party on every render, with the brand blue
	// hardcoded in the query string so it followed neither the token nor the theme.
	const initials = `${userContext.user?.NombreSimple?.[0] || ''}${userContext.user?.ApellidoSimple?.[0] || ''}`.toUpperCase() || '?';

	const handlePerfilOption = () => {
		sidebarContext.handleChangeCurrentPage('Perfil');
		setIsUserMenuOpen(false);
		if (sidebarContext.isMobile) sidebarContext.closeSidebar();
		history.push('/dashboard/profile');
	}

	const handleLogout = () => {
		setIsUserMenuOpen(false);
		userContext.signOut();
		if (sidebarContext.isMobile) sidebarContext.closeSidebar();
		history.push('/login');
	}

	const toggleUserMenu = () => setIsUserMenuOpen((value) => !value);

	// Etiqueta de ambiente: DEV | STAGING | LIVE (siempre visible)
	const environmentLabel = getEnvironmentLabel();
	const envBadgeTone =
		environmentLabel === 'LIVE'
			? 'bg-success/15 text-success'
			: environmentLabel === 'STAGING'
				? 'bg-warning/15 text-warning'
				: 'bg-destructive/15 text-destructive';

	// Cerrar menú al hacer click fuera
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
				setIsUserMenuOpen(false);
			}
		};

		if (isUserMenuOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isUserMenuOpen]);

	// Verificar si hay nuevas versiones
	useEffect(() => {
		const lastSeenVersion = localStorage.getItem('lastSeenVersion');
		const currentVersion = packageJson.version;

		if (lastSeenVersion !== currentVersion) {
			setHasNewUpdates(true);
		}
	}, []);

	const toggleSidebar = () => {
		if (!sidebarContext.isMobile) {
			sidebarContext.showSidebar(!sidebarContext.sidebarOpen);
		}
	};

	return (
	<div
		className={cn(
			'fixed left-0 top-0 z-[1000] flex h-screen flex-col overflow-hidden',
			'border-r border-sidebar-border bg-sidebar transition-[width,transform] duration-300 ease-out',
			isOpen ? WIDTH_OPEN : WIDTH_CLOSED,
			isOpen ? MOBILE_OPEN : MOBILE_CLOSED
		)}>
		{/* Marca + control de plegado */}
		<div className={cn('flex h-16 shrink-0 items-center px-4', isOpen ? 'justify-between' : 'justify-center')}>
			<img
				src={isOpen ? '/img/logoboxitv2.png' : '/img/logo-b-v2.png'}
				alt="Boxit"
				className={cn('object-contain', isOpen ? 'h-11 w-24' : 'size-9')}
			/>
			{ !sidebarContext.isMobile && (
				<button
					type="button"
					className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					onClick={toggleSidebar}
					aria-label={isOpen ? translate("sidebar.aria.collapse") : translate("sidebar.aria.expand")}
				>
					{isOpen ? <LuChevronsLeft size={16} /> : <LuChevronsRight size={16} />}
				</button>
			)}
			{ sidebarContext.isMobile &&
				<button
					type="button"
					className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent"
					onClick={ () => sidebarContext.closeSidebar() }
					aria-label={translate("sidebar.aria.collapse")}
				>
					<LuX size={22} />
				</button>
			}
		</div>

		{/* Navegación */}
		<nav className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 pt-1">
			<ul className="m-0 list-none space-y-0.5 p-0">
			{
				SidebarData && SidebarData.map((item, index) => {
					return <li key={index}>
								<Submenu item={item} />
							</li>
				})
			}
			</ul>
		</nav>

		{/* Utilidad: novedades. Es una acción secundaria, así que va en 13px y en
		    color apagado, para no competir con la navegación. */}
		<div className="shrink-0 px-3 pb-2">
			<Tooltip label={!isOpen ? translate("navbar.news") : null}>
				<button
					type="button"
					className={cn(
						'relative flex h-9 w-full items-center gap-3 rounded-lg px-3',
						'text-[13px] text-muted-foreground transition-colors',
						'hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
						'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						!isOpen && 'justify-center'
					)}
					onClick={() => {
						setIsNewsModalOpen(true);
						setHasNewUpdates(false);
						localStorage.setItem('lastSeenVersion', packageJson.version);
						if (sidebarContext.isMobile) sidebarContext.closeSidebar();
					}}
				>
					<span className="relative flex size-5 shrink-0 items-center justify-center">
						<LuMegaphone size={18} />
						{/* On the glyph's own corner, so it travels with it instead of
						    being pinned to offsets calibrated for the expanded row. */}
						{hasNewUpdates && (
							<span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-destructive ring-2 ring-sidebar" />
						)}
					</span>
					{isOpen && <span className="truncate">{translate("navbar.news")}</span>}
				</button>
			</Tooltip>
		</div>

		{/* Cuenta al pie. Sin tarjeta: su relleno se diferenciaba del sidebar en
		    un 1% de luminancia, así que el borde hacía todo el trabajo y el
		    resultado era un rectángulo flotando sobre su propio color. */}
		<div className="shrink-0 border-t border-sidebar-border p-2">
			<div ref={userMenuRef} className="relative">
				<Tooltip label={!isOpen ? (userContext.user?.Usuario || translate("sidebar.user")) : null}>
					<button
						type="button"
						className={cn(
							'flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors',
							'hover:bg-sidebar-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
							!isOpen && 'justify-center',
							isUserMenuOpen && 'bg-sidebar-accent'
						)}
						onClick={toggleUserMenu}
						aria-expanded={isUserMenuOpen}
						aria-haspopup="menu"
					>
						{userContext.user?.ProfileImage ? (
							<img
								src={userContext.user.ProfileImage}
								alt=""
								className="size-8 shrink-0 rounded-full object-cover"
							/>
						) : (
							<span
								aria-hidden="true"
								className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground"
							>
								{initials}
							</span>
						)}

						{isOpen && (
							<span className="min-w-0 flex-1">
								<span className="block truncate text-[13px] font-semibold leading-tight text-foreground">
									{userContext.user?.NombreSimple ? handleName() : translate("sidebar.user")}
								</span>
								<span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
									{countryFlag && (
										<img src={countryFlag} alt="" aria-hidden="true" className="h-3 w-4 shrink-0 rounded-sm object-cover" />
									)}
									<span className="truncate">{userContext.user?.Usuario || 'usuario@boxit.com'}</span>
								</span>
							</span>
						)}

						{isOpen && (
							<LuChevronDown
								size={14}
								className={cn(
									'shrink-0 text-muted-foreground transition-transform duration-200',
									isUserMenuOpen && 'rotate-180'
								)}
							/>
						)}
					</button>
				</Tooltip>

				<UserMenu
					isOpen={isUserMenuOpen}
					onClose={() => setIsUserMenuOpen(false)}
					onProfileClick={handlePerfilOption}
					onLogout={handleLogout}
					variant="sidebar"
				/>
			</div>

			{/* El entorno también en plegado: es justo cuando más fácil es olvidar
			    en cuál estás. */}
			<div className={cn('mt-1 flex items-center gap-2 px-2', isOpen ? 'justify-between' : 'justify-center')}>
				<span className={cn('rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide', envBadgeTone)}>
					{isOpen ? environmentLabel : environmentLabel.slice(0, 3)}
				</span>
				{isOpen && <span className="text-[11px] text-muted-foreground">v{packageJson.version}</span>}
			</div>
		</div>

		<NewsModal
			isOpen={isNewsModalOpen}
			onClose={() => setIsNewsModalOpen(false)}
			lastSeenVersion={localStorage.getItem('lastSeenVersion')}
		/>
		</div>
	);
}

export default Sidebar;
